import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { FaSearch, FaSyncAlt } from "react-icons/fa";
import { StandardURL } from "../App";

const ViewVacatePage = () => {
  const [vacateData, setVacateData] = useState([]);
  const [userDetails, setUserDetails] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchVacateData = async () => {
    if (!userDetails.trim()) {
      toast.error("Please enter user details (ID, email, or UUID) to search.");
      return;
    }

    setIsLoading(true);
    setError(null);
    setVacateData([]); // Clear previous data
    const token = localStorage.getItem("token");

    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/vacatinghouse/users/${userDetails}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.response && Array.isArray(response.data.response)) {
        setVacateData(response.data.response);
        toast.success("Vacate data fetched successfully!");
      } else {
        setVacateData([]);
        setError("No vacate data found for this user.");
      }
    } catch (err) {
      setError(err.response?.data?.response || "Failed to fetch vacate data. Please check the user details.");
      toast.error(err.response?.data?.response || "Failed to fetch vacate data.");
      console.error("Error fetching vacate data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setUserDetails("");
    setVacateData([]);
    setError(null);
  };

  const handleEnterKeyPress = (e) => {
    if (e.key === "Enter") {
      fetchVacateData();
    }
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="container mx-auto px-4 py-8 bg-gray-100 min-h-screen">
          <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">View Vacate Information</h1>

          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <div className="flex flex-col md:flex-row items-center mb-4 space-y-4 md:space-y-0 md:space-x-4">
              <input
                type="text"
                value={userDetails}
                onChange={(e) => setUserDetails(e.target.value)}
                onKeyPress={handleEnterKeyPress}
                placeholder="Enter User Details (ID or email)"
                className="border border-gray-300 rounded-md py-3 px-4 w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out"
              />
              <div className="flex space-x-2 w-full md:w-auto">
                <button
                  onClick={fetchVacateData}
                  disabled={isLoading}
                  className={`flex items-center justify-center w-full md:w-auto py-3 px-6 rounded-md font-semibold transition duration-150 ease-in-out
                    ${isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
                >
                  <FaSearch className="mr-2" />
                  {isLoading ? "Searching..." : "Search"}
                </button>
                <button
                  onClick={handleReset}
                  className="flex items-center justify-center w-full md:w-auto py-3 px-6 rounded-md font-semibold transition duration-150 ease-in-out bg-gray-500 hover:bg-gray-600 text-white"
                >
                  <FaSyncAlt className="mr-2" />
                  Reset
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8">
            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <Oval height={50} width={50} color="#4F46E5" />
              </div>
            ) : error ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-center" role="alert">
                <span className="font-semibold">{error}</span>
              </div>
            ) : vacateData.length > 0 ? (
              vacateData.map((record, index) => (
                <div key={index} className="bg-white shadow-lg rounded-lg p-8 mb-6 animate-fade-in">
                  <p className="text-2xl font-bold mb-6 text-gray-800">Vacate Record #{index + 1}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 text-gray-700">
                    <div className="bg-gray-50 p-4 rounded-md shadow-sm">
                      <p className="font-semibold">Total Amount for Repairs:</p>
                      <p className="text-lg">Ksh {record.totalamountrepairs}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md shadow-sm">
                      <p className="font-semibold">Service Fee:</p>
                      <p className="text-lg">Ksh {record.servicefee}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md shadow-sm">
                      <p className="font-semibold">Total Fee:</p>
                      <p className="text-lg">Ksh {record.totalfee}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md shadow-sm">
                      <p className="font-semibold">Repair Charges:</p>
                      <p className="text-lg">Ksh {record.repaircharges}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md shadow-sm">
                      <p className="font-semibold">Landlord Status:</p>
                      <p className="text-lg">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${record.completestatus === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {record.completestatus}
                        </span>
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md shadow-sm">
                      <p className="font-semibold">Your Vacate Status:</p>
                      <p className="text-lg">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${record.vacatingstatus === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {record.vacatingstatus}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white p-6 rounded-lg shadow-md text-center text-gray-500">
                <p>Enter a user's details and click 'Search' to view their vacate information.</p>
              </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default ViewVacatePage;